import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CLOUD_LIMIT_USD, usd, type CloudSpend } from '@couch/shared';
import { useState, type SyntheticEvent } from 'react';
import { errorMessage } from '../../api/client';
import { CLOUD_SPEND_KEY, cloudApi } from '../../api/cloud';
import { CloudCostWarning } from '../machines/CloudBadge';

const time = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** "This month at RunPod: $3.12 of your $20.00 limit · checked …" — shared with the pod's RunPod panel. */
export function spendLine(spend: CloudSpend): string {
  if (spend.spentUsd === null) return `This month (${spend.month}) at RunPod: not checked yet.`;
  const limit = spend.limitUsd === null ? ' (no limit set)' : ` of your ${usd(spend.limitUsd)} limit`;
  return `This month (${spend.month}) at RunPod: ${usd(spend.spentUsd)}${limit}${spend.checkedAt ? ` · checked ${time(spend.checkedAt)}` : ''}.`;
}

/** What the guard did and what keeps it from working (doc 60). */
export function GuardNotes({ spend }: { spend: CloudSpend }) {
  return (
    <>
      {spend.error && <p role="alert" className="form-error">{spend.error}</p>}
      {spend.lastStop && <p role="status" className="cloud-warning">Couch stopped {spend.lastStop.pods.join(', ')} on {time(spend.lastStop.at)}: {spend.lastStop.reason}</p>}
    </>
  );
}

/** Account → Cloud spending limit (doc 60): Couch stops its pods when RunPod's bill for them reaches it. */
export function CloudLimitPanel() {
  const queryClient = useQueryClient();
  const spend = useQuery({ queryKey: CLOUD_SPEND_KEY, queryFn: cloudApi.spend });
  // Only the user's edit is state; the field shows the stored limit until then (CLAUDE.md: never seed from loaded data).
  const [edit, setEdit] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const stored = spend.data?.spend.limitUsd;
  const value = edit ?? (stored === null || stored === undefined ? '' : String(stored));
  const refresh = (data: { spend: CloudSpend }) => { queryClient.setQueryData(CLOUD_SPEND_KEY, data); };
  const save = useMutation({
    mutationFn: () => cloudApi.setLimit(value.trim() === '' ? null : Number(value)),
    onSuccess: async () => { setEdit(null); setSaved(true); await queryClient.invalidateQueries({ queryKey: CLOUD_SPEND_KEY }); },
  });
  const check = useMutation({ mutationFn: cloudApi.checkSpend, onSuccess: refresh });
  const submit = (e: SyntheticEvent) => { e.preventDefault(); setSaved(false); save.mutate(); };
  return (
    <section className="panel cloud-limit" aria-labelledby="cloud-limit-title">
      <h2 id="cloud-limit-title">Cloud spending limit</h2>
      <p className="muted">
        When RunPod&apos;s bill for the pods Couch made for you reaches this in a month, Couch stops them and starts no new ones.
        RunPod&apos;s billing can lag behind by about an hour.
      </p>
      {spend.data && <p>{spendLine(spend.data.spend)}</p>}
      {spend.data && <GuardNotes spend={spend.data.spend} />}
      <form className="cloud-limit-form" onSubmit={submit}>
        <label htmlFor="cloud-limit">Monthly limit in US dollars (empty = no limit)</label>
        <input id="cloud-limit" type="number" min={CLOUD_LIMIT_USD.min} max={CLOUD_LIMIT_USD.max} step="1" value={value} onChange={(e) => { setEdit(e.target.value); setSaved(false); }} />
        <div className="pod-actions">
          <button type="submit" disabled={save.isPending}>Save</button>
          <button type="button" className="button-quiet" disabled={check.isPending} onClick={() => { check.mutate(); }}>Check RunPod now</button>
        </div>
      </form>
      {saved && <p role="status" className="success">Saved.</p>}
      {[spend.error, save.error, check.error].filter(Boolean).map((e, n) => <p key={n} role="alert" className="form-error">{errorMessage(e)}</p>)}
      <CloudCostWarning />
    </section>
  );
}
